/**
 * The seven admin sections, each reading only what it draws.
 *
 * Every one is its own route, so the provider record and the audit trail are
 * read when their section opens rather than all at once behind a tab strip.
 *
 * @module router/admin/AdminSectionRoutes
 */

import React, { useEffect, useMemo, useRef, useState } from "react";

import { useNavigate } from "react-router";

import { AccessPolicySettings } from "@/components/admin/AccessPolicySettings";
import {
  auditFiltersActive,
  type AuditFilterValues,
  auditQueryFor,
  NO_AUDIT_FILTERS,
} from "@/components/admin/audit-trail-filters";
import { AuditTrail } from "@/components/admin/AuditTrail";
import { AuditTrailFilters } from "@/components/admin/AuditTrailFilters";
import { InviteUserForm } from "@/components/admin/InviteUserForm";
import { ProgrammeDeliveryTable } from "@/components/admin/ProgrammeDeliveryTable";
import { ProviderSettingsForm } from "@/components/admin/ProviderSettingsForm";
import { TenantDisciplines } from "@/components/admin/TenantDisciplines";
import { TenantUserTable } from "@/components/admin/TenantUserTable";
import { useAccessPolicy } from "@/components/admin/useAccessPolicy";
import { useAuditTrail } from "@/components/admin/useAuditTrail";
import { useDeliveryStatus } from "@/components/admin/useDeliveryStatus";
import { useProviderSettings } from "@/components/admin/useProviderSettings";
import { useTenantUsers } from "@/components/admin/useTenantUsers";
import { AssignmentManagement } from "@/components/teams/AssignmentManagement";
import { Button, Divider, ErrorAlert, Heading, MutedText } from "@/components/ui";
import { ProgrammeTeamsBrowser } from "@/router/programmes/ProgrammeTeamsRoutes";
import { useWorkspaceOutletContext } from "@/router/workspace-context";

/** The shell's read failed, so an empty section here would be a claim nobody checked. */
function WorkspaceReadFailed({
  what,
  error,
  onRetry,
  "data-testid": testId,
}: {
  what: string;
  error: Error;
  onRetry: () => Promise<void>;
  "data-testid": string;
}): React.ReactElement {
  return (
    <ErrorAlert data-testid={testId}>
      <span className="flex flex-wrap items-center gap-3">
        <span>
          {what} could not be loaded. {error.message}
        </span>
        <Button
          size="sm"
          variant="outline"
          onClick={() => {
            onRetry().catch(() => undefined);
          }}
        >
          Try again
        </Button>
      </span>
    </ErrorAlert>
  );
}

export function AdminUsersRoute(): React.ReactElement {
  const { client, session, platformUsers, platformDataError, refreshPlatformWorkspace } =
    useWorkspaceOutletContext();
  const users = useTenantUsers(client, session.tenant.id, refreshPlatformWorkspace);
  const [inviting, setInviting] = useState(false);
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Heading level={2} size="md">
          Users
        </Heading>
        <Button
          size="sm"
          variant="primary"
          icon="user-plus"
          onClick={() => {
            setInviting(true);
          }}
          data-testid="admin-invite-user"
        >
          Invite user
        </Button>
      </div>
      <InviteUserForm
        open={inviting}
        onClose={() => {
          setInviting(false);
        }}
        pending={users.pending}
        error={users.error}
        onInvite={users.invite}
        tenantName={session.tenant.name}
        directoryBound={Boolean(session.tenant.entraTenantId)}
        impersonating={Boolean(session.impersonatedBy)}
        existingEmails={platformUsers.map((user) => user.email)}
      />
      {platformDataError ? (
        <WorkspaceReadFailed
          what="The tenant's users"
          error={platformDataError}
          onRetry={refreshPlatformWorkspace}
          data-testid="admin-users-error"
        />
      ) : (
        <TenantUserTable
          users={platformUsers}
          pending={users.pending}
          onChangeRole={users.changeRole}
          onToggleProgrammeCreation={users.toggleProgrammeCreation}
          onToggleStatus={users.toggleStatus}
          onResendInvitation={users.resendInvitation}
          onChangeEmail={users.changeEmail}
          error={users.tableError}
        />
      )}
    </div>
  );
}

export function AdminProgrammesRoute(): React.ReactElement {
  const navigate = useNavigate();
  const {
    client,
    session,
    platformProgrammes,
    platformUsers,
    platformDataError,
    refreshPlatformWorkspace,
  } = useWorkspaceOutletContext();
  const delivery = useDeliveryStatus(client, platformProgrammes, refreshPlatformWorkspace);
  const [picked, setPicked] = useState<string | null>(null);
  // Close sits inside the section it unmounts, so focus has to be handed back
  // to the row's own button rather than left to fall to the body.
  const restoreFocusTo = useRef<string | null>(null);
  useEffect(() => {
    const id = restoreFocusTo.current;
    if (id) {
      restoreFocusTo.current = null;
      document.querySelector<HTMLElement>(`[data-testid="${id}"]`)?.focus();
    }
  });
  // A programme that has left the tenant cannot be the one being managed, so
  // the pick reads as nothing rather than being cleared from an effect. A
  // filter that only hides it is not the same thing: the editor stays open.
  const managedProgrammeId =
    picked && platformProgrammes.some((programme) => programme.id === picked) ? picked : null;

  if (platformDataError) {
    return (
      <WorkspaceReadFailed
        what="The programmes"
        error={platformDataError}
        onRetry={refreshPlatformWorkspace}
        data-testid="admin-programmes-error"
      />
    );
  }

  return (
    <div className="space-y-6">
      <ProgrammeDeliveryTable
        programmes={platformProgrammes}
        delivery={delivery}
        onCreateProgramme={() => navigate("/programmes/new")}
        onViewProgramme={(programmeId) => navigate(`/programmes/${programmeId}/overview/identity`)}
        onManageBuilders={(programmeId) => {
          setPicked((current) => (current === programmeId ? null : programmeId));
        }}
        managingProgrammeId={managedProgrammeId}
      />
      {managedProgrammeId ? (
        <section
          aria-labelledby="admin-module-builders-heading"
          data-testid="admin-module-builders-editor"
        >
          <Divider className="mb-6" />
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <div>
              <Heading level={2} size="md" id="admin-module-builders-heading">
                Module Builder assignments
              </Heading>
              <MutedText size="caption">
                Edit programme builder access and module ownership.
              </MutedText>
            </div>
            <Button
              variant="ghost"
              size="sm"
              icon="x"
              onClick={() => {
                restoreFocusTo.current = `admin-manage-builders-${managedProgrammeId}`;
                setPicked(null);
              }}
            >
              Close
            </Button>
          </div>
          <AssignmentManagement
            client={client}
            session={session}
            programmes={platformProgrammes}
            users={platformUsers}
            fixedProgrammeId={managedProgrammeId}
            onChanged={refreshPlatformWorkspace}
          />
        </section>
      ) : null}
    </div>
  );
}

export function AdminProviderRoute(): React.ReactElement {
  const { client, session } = useWorkspaceOutletContext();
  const provider = useProviderSettings(client, session.tenant.id);
  return (
    <ProviderSettingsForm
      record={provider.record}
      draft={provider.draft}
      setDraft={provider.setDraft}
      onContactChange={provider.updateContact}
      pending={provider.pending}
      feedback={provider.feedback}
      onSave={() => provider.save()}
      loadError={provider.loadError}
      onRetry={provider.reload}
    />
  );
}

export function AdminAccessRoute(): React.ReactElement {
  const { client, session } = useWorkspaceOutletContext();
  const access = useAccessPolicy(client, session.tenant.id);
  return (
    <AccessPolicySettings
      policy={access.policy}
      loading={access.loading}
      saving={access.saving}
      onChange={access.setPolicy}
      feedback={access.feedback}
      failed={access.failed}
      onSave={() => access.save()}
      onRetry={access.reload}
    />
  );
}

/** Programmes per discipline, or undefined until the workspace read has an answer. */
function disciplineUsage(
  programmes: readonly { discipline: string }[],
  settled: boolean,
): ReadonlyMap<string, number> | undefined {
  if (!settled) {
    return undefined;
  }
  const usage = new Map<string, number>();
  programmes.forEach(({ discipline }) => {
    if (discipline) {
      usage.set(discipline, (usage.get(discipline) ?? 0) + 1);
    }
  });
  return usage;
}

export function AdminDisciplinesRoute(): React.ReactElement {
  const { client, session, platformProgrammes, platformDataError } = useWorkspaceOutletContext();
  const provider = useProviderSettings(client, session.tenant.id);
  const usage = useMemo(
    () => disciplineUsage(platformProgrammes, !platformDataError),
    [platformProgrammes, platformDataError],
  );
  return (
    <TenantDisciplines
      disciplines={provider.record?.settings.disciplines ?? []}
      ready={Boolean(provider.record) && !provider.pending}
      feedback={provider.disciplineFeedback}
      onSave={(next) => provider.saveDisciplines(next)}
      saving={provider.disciplinesSaving}
      loadError={provider.loadError}
      onRetry={provider.reload}
      usage={usage}
      reportHref="/reports/disciplines"
    />
  );
}

export function AdminTeamsRoute(): React.ReactElement {
  const { platformDataError, refreshPlatformWorkspace } = useWorkspaceOutletContext();
  if (platformDataError) {
    return (
      <WorkspaceReadFailed
        what="The programmes"
        error={platformDataError}
        onRetry={refreshPlatformWorkspace}
        data-testid="admin-teams-error"
      />
    );
  }
  return <ProgrammeTeamsBrowser base="/admin/teams" autoSelectFirst />;
}

export function AdminAuditRoute(): React.ReactElement {
  const {
    client,
    session,
    platformUsers,
    platformProgrammes,
    platformModules,
    platformDataError,
    refreshPlatformWorkspace,
  } = useWorkspaceOutletContext();
  const [filters, setFilters] = useState<AuditFilterValues>(NO_AUDIT_FILTERS);
  const { events, total, error, pending, reload } = useAuditTrail(client, auditQueryFor(filters));
  const moduleNames = useMemo(
    () =>
      new Map(
        platformModules.map((row) => [
          row.moduleId,
          { title: row.moduleTitle, programmeTitle: row.programmeTitle },
        ]),
      ),
    [platformModules],
  );
  const userNames = useMemo(
    () => new Map(platformUsers.map((user) => [user.id, user.displayName])),
    [platformUsers],
  );
  const programmeNames = useMemo(
    () => new Map(platformProgrammes.map((programme) => [programme.id, programme.title])),
    [platformProgrammes],
  );
  return (
    <div className="space-y-4">
      <AuditTrail
        filtered={auditFiltersActive(filters)}
        filters={
          <AuditTrailFilters
            values={filters}
            onChange={setFilters}
            users={platformUsers}
            programmes={platformProgrammes}
            shown={pending || error ? undefined : { shown: events.length, total }}
          />
        }
        events={events}
        error={error}
        pending={pending}
        onRetry={() => {
          reload();
        }}
        userNames={userNames}
        programmeNames={programmeNames}
        moduleNames={moduleNames}
        tenantName={session.tenant.name}
        namesError={platformDataError?.message}
        onRetryNames={() => {
          refreshPlatformWorkspace();
        }}
      />
    </div>
  );
}
